
import type { FishCaught, Fishing } from "../net/messages";
import type { FishingClipName, MotionMode } from "../render/creaturemesh";
import { elem, styleInstaller } from "./chrome";
import { HEADING_MASK, HEADING_STEPS } from "../sim/fixed";

export interface FishingActions {
  /** Cast, or pull. One call for both -- the server knows which. */
  onFish?: () => void;
}

/** `Fishing.state`. Wire values, fixed by the schema. */
export const FISHING_IDLE = 0;
export const FISHING_WAITING = 1;
export const FISHING_BITING = 2;

/** `FishCaught.outcome`. Wire values, fixed by the schema. */
export const FISH_LANDED = 0;
export const FISH_NOTHING = 1;
export const FISH_TOO_SLOW = 2;
export const FISH_SLIPPED = 3;
export const FISH_TOO_STRONG = 4;

export function describeCatch(caught: FishCaught): string {
  switch (caught.outcome) {
    case FISH_LANDED: {
      const name = caught.name || `fish #${caught.vnum}`;
      // The two chat lines the server actually sends on a landed catch,
      // joined: `FISHING_SUCCESS1` (`locale_game.txt:131`) and the length
      // report `fishing.cpp:640` composes in the original's own words.
      return `You captured ${name}! The length of the captured fish is ${lengthCm(caught.length)}.`;
    }
    case FISH_NOTHING:
      return "Nothing had taken the bait.";
    case FISH_TOO_SLOW:
      return "You were too slow; it got away.";
    case FISH_SLIPPED:
      return "It slipped the hook — your timing was off.";
    case FISH_TOO_STRONG:
      return "It was too strong for this rod.";
    default:
      return "The line came back empty.";
  }
}

/** Hundredths of a centimetre as the two decimals the original prints
 *  (`fishing.cpp:640`'s `%.2fcm` -- no space before the unit). */
/** `FishingAct.act`: the original's subheaders, in its order
 *  (`packet.h:1816-1820`). Wire values, fixed by the schema. */
export const FISHING_ACT_START = 0;
export const FISHING_ACT_STOP = 1;
export const FISHING_ACT_REACT = 2;
export const FISHING_ACT_SUCCESS = 3;
export const FISHING_ACT_FAIL = 4;

/** `RecvFishing`'s answer for one body (`PythonNetworkStreamPhaseGame.cpp:3738-3759`):
 *  the one-shot it pushes and whether the body is left fishing. A start throws
 *  (`InstanceBaseMotion.cpp:49-67`); a stop cancels and a bite tugs only a body
 *  that is fishing (`:69-78`, guarded at `PythonNetworkStreamPhaseGame.cpp:3741-3750`);
 *  a catch or a fail plays whatever the body was doing (`InstanceBaseMotion.cpp:79-88`). */
export function fishingAct(act: number, fishing: boolean): { clip: FishingClipName | null; fishing: boolean } {
  switch (act) {
    case FISHING_ACT_START:
      return { clip: "fishingThrow", fishing: true };
    case FISHING_ACT_STOP:
      return { clip: fishing ? "fishingStop" : null, fishing: false };
    case FISHING_ACT_REACT:
      return { clip: fishing ? "fishingReact" : null, fishing };
    case FISHING_ACT_SUCCESS:
      return { clip: "fishingCatch", fishing: false };
    case FISHING_ACT_FAIL:
      return { clip: "fishingFail", fishing: false };
    default:
      return { clip: null, fishing };
  }
}

/** The loops this player's body is drawn in: the fishing wait while a line is
 *  out (`InstanceBaseMotion.cpp:67,77`), the rod's own set while the rod the
 *  line state names is the weapon worn (`InstanceBase.cpp:2885-2895`). */
export function lineMode(state: number, rodVnum: number, wornWeapon: number): MotionMode {
  if (state !== FISHING_IDLE) return "fishingLine";
  return rodVnum !== 0 && rodVnum === wornWeapon ? "fishing" : "general";
}

/** `c_fFishingDistance`: how far ahead of the angler the float lands
 *  (`InstanceBaseMotion.cpp:7`), in mm. */
export const FISHING_DISTANCE_MM = 600 * 10; // InstanceBaseMotion.cpp:7

const FISHING_ROT_STEP_DEG = 10; // InstanceBaseMotion.cpp:95
const FISHING_ROT_MAX_DEG = 180; // InstanceBaseMotion.cpp:95

/** The point `distanceMm` ahead of (xMm, yMm) along a 4096-step heading, as the
 *  movement step faces it. */
function ahead(heading: number, xMm: number, yMm: number, distanceMm: number): { xMm: number; yMm: number } {
  const radians = (heading / HEADING_STEPS) * Math.PI * 2;
  return { xMm: xMm + distanceMm * Math.cos(radians), yMm: yMm + distanceMm * Math.sin(radians) };
}

/** `GetFishingRot` (`InstanceBaseMotion.cpp:90-118`): the facing a cast is
 *  sent with. From the angler's own facing outwards in ten-degree steps, one
 *  side and then the other, the first heading whose float would land on water;
 *  null when none within half a turn does, and then no cast is sent
 *  (`PythonPlayerInput.cpp:541-546`). */
export function fishingHeading(
  heading: number,
  xMm: number,
  yMm: number,
  isWater: (xMm: number, yMm: number) => boolean,
): number | null {
  for (let deg = 0; deg <= FISHING_ROT_MAX_DEG; deg += FISHING_ROT_STEP_DEG) {
    for (const turned of [deg, -deg]) {
      const candidate = Math.round(heading + (turned / 360) * HEADING_STEPS) & HEADING_MASK;
      const at = ahead(candidate, xMm, yMm, FISHING_DISTANCE_MM);
      if (isWater(at.xMm, at.yMm)) return candidate; // InstanceBaseMotion.cpp:102-114
    }
  }
  return null; // InstanceBaseMotion.cpp:117
}

/** `StartFishing`'s float (`InstanceBaseMotion.cpp:53-64`): `FISHING_DISTANCE_MM`
 *  ahead along the cast's facing, on the water's surface there, or at the
 *  angler's own height where the map holds no water height. */
export function floatPoint(
  heading: number,
  xMm: number,
  yMm: number,
  waterHeightAt: (xMm: number, yMm: number) => number | null,
  bodyZMm: number,
): { xMm: number; yMm: number; zMm: number } {
  const at = ahead(heading, xMm, yMm, FISHING_DISTANCE_MM);
  return { ...at, zMm: waterHeightAt(at.xMm, at.yMm) ?? bodyZMm }; // InstanceBaseMotion.cpp:59-60
}

/** The `FishingEffectFlag` events of the two clips a cast plays, which
 *  `ProcessMotionEventEffect` spawns at the float rather than on the body
 *  (`ActorInstanceMotionEvent.cpp:175-184`). */
export const FLOAT_EFFECTS: Readonly<Record<"fishingThrow" | "fishingWait", { effect: string; atS: number }>> = {
  fishingThrow: { effect: "fishing_throw", atS: 0.824006 }, // throw.msa:12,14
  fishingWait: { effect: "fishing_wait", atS: 0 }, // fishing_wait.msa:12,14
};

/** Where a float effect is spawned, in world millimetres. */
export interface FloatPosition {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
}

/** What one body's `m_iFishingEffectID` slot asks of the effect layer: spawn an
 *  effect at the float under a fresh instance handle, or deactivate one. */
export type FloatEffectCommand =
  | { readonly kind: "spawn"; readonly instance: number; readonly effect: string; readonly at: FloatPosition }
  | { readonly kind: "deactivate"; readonly instance: number };

interface FloatBody {
  readonly at: FloatPosition;
  splashAtMs: number | null;
  loopAtMs: number | null;
  loopMs: number;
  instance: number | null;
}

/** Every angler's `m_iFishingEffectID` (`ActorInstance.h:756`). A clip pushed on
 *  the body deactivates the effect in the slot (`ActorInstanceMotion.cpp:699-705`);
 *  a `FishingEffectFlag` event deactivates it and spawns its own effect at the
 *  float in its place (`ActorInstanceMotionEvent.cpp:175-184`). The throw's
 *  splash fires `FLOAT_EFFECTS.fishingThrow.atS` into the throw, and the wait's
 *  ripple at the start of every wait loop, which begins when the throw or a
 *  bite's tug ends (`InstanceBaseMotion.cpp:64-67,76-78`) and rewinds to frame 0
 *  each time its loop ends (`ActorInstanceMotion.cpp:174-183`). */
export class FishingFloatEffects {
  private readonly bodies = new Map<number, FloatBody>();
  private nextInstance = 1;

  /** One `FishingAct` on `entityId`: `fishing` is whether the body is left
   *  fishing, `at` the float a start drops, `clipMs` the body's clip lengths. */
  act(
    entityId: number,
    act: number,
    fishing: boolean,
    nowMs: number,
    at: FloatPosition | null,
    clipMs: (clip: FishingClipName) => number,
  ): FloatEffectCommand[] {
    const out: FloatEffectCommand[] = [];
    const body = this.bodies.get(entityId);
    if (body !== undefined && body.instance !== null) {
      out.push({ kind: "deactivate", instance: body.instance });
      body.instance = null;
    }
    if (act === FISHING_ACT_START && at !== null) {
      this.bodies.set(entityId, {
        at,
        splashAtMs: nowMs + FLOAT_EFFECTS.fishingThrow.atS * 1000,
        loopAtMs: nowMs + clipMs("fishingThrow"),
        loopMs: clipMs("fishingWait"),
        instance: null,
      });
    } else if (!fishing) {
      this.bodies.delete(entityId);
    } else if (act === FISHING_ACT_REACT && body !== undefined) {
      body.splashAtMs = null;
      body.loopAtMs = nowMs + clipMs("fishingReact");
    }
    return out;
  }

  /** The events due by `nowMs`. A body `present` no longer answers for is
   *  dropped without another spawn. */
  step(nowMs: number, present: (entityId: number) => boolean): FloatEffectCommand[] {
    const out: FloatEffectCommand[] = [];
    for (const [entityId, body] of this.bodies) {
      if (!present(entityId)) {
        this.bodies.delete(entityId);
        continue;
      }
      if (body.splashAtMs !== null && nowMs >= body.splashAtMs) {
        body.splashAtMs = null;
        this.fire(body, FLOAT_EFFECTS.fishingThrow.effect, out);
      }
      if (body.loopAtMs !== null && nowMs >= body.loopAtMs + FLOAT_EFFECTS.fishingWait.atS * 1000) {
        this.fire(body, FLOAT_EFFECTS.fishingWait.effect, out);
        body.loopAtMs = body.loopMs > 0
          ? body.loopAtMs + (Math.floor((nowMs - body.loopAtMs) / body.loopMs) + 1) * body.loopMs
          : null;
      }
    }
    return out;
  }

  private fire(body: FloatBody, effect: string, out: FloatEffectCommand[]): void {
    if (body.instance !== null) out.push({ kind: "deactivate", instance: body.instance });
    body.instance = this.nextInstance++;
    out.push({ kind: "spawn", instance: body.instance, effect, at: body.at });
  }
}

export function lengthCm(hundredths: number): string {
  return `${(hundredths / 100).toFixed(2)}cm`;
}

/** What the button should say for a given line state. */
export function buttonLabel(state: number): string {
  if (state === FISHING_BITING) return "Pull!";
  if (state === FISHING_WAITING) return "Pull in";
  return "Cast";
}

const STYLE_ID = "fishing-style";

const STYLE = `
.fishing {
  position: fixed;
  right: 12px;
  top: 300px;
  z-index: 58;
  width: 196px;
  padding: 9px 11px 10px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.86);
  border: 1px solid rgba(130, 190, 210, 0.24);
  pointer-events: auto;
  font: 12px/1.45 system-ui, sans-serif;
  color: #d8dee6;
}
.fishing[hidden] { display: none; }
.fishing-title {
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #84b6c4;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
}
.fishing-state { color: #9fb6d4; }
.fishing-state-biting { color: #ffd27a; font-weight: 600; }
.fishing-rod { margin-bottom: 4px; }
.fishing-none { color: #8fa0b4; margin: 4px 0 6px; }
.fishing-meter-label {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #9fb6d4;
}
.fishing-meter-track {
  height: 5px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
  margin-bottom: 4px;
}
.fishing-meter-fill { height: 100%; width: 0; background: #7fc4d8; }
.fishing-window {
  height: 7px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
  margin: 5px 0;
}
.fishing-window[hidden] { display: none; }
.fishing-window-fill { height: 100%; width: 0; background: #ffd27a; }
.fishing-last { color: #b8c6d8; font-size: 11px; min-height: 2.6em; }
.fishing-button {
  width: 100%;
  margin-top: 6px;
  padding: 5px 0;
  border-radius: 6px;
  border: 1px solid rgba(130, 190, 210, 0.34);
  background: rgba(130, 190, 210, 0.1);
  color: #d6eff6;
  font: inherit;
  cursor: pointer;
}
.fishing-button:hover:not(:disabled) { background: rgba(130, 190, 210, 0.22); }
.fishing-button:disabled { opacity: 0.35; cursor: default; }
.fishing-button-biting { border-color: #ffd27a; color: #ffe6b0; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export class FishingPanel {
  private readonly root: HTMLElement;
  private readonly state: HTMLElement;
  private readonly rod: HTMLElement;
  private readonly none: HTMLElement;
  private readonly practiceLabel: HTMLElement;
  private readonly practiceFill: HTMLElement;
  private readonly window_: HTMLElement;
  private readonly windowFill: HTMLElement;
  private readonly last: HTMLElement;
  private readonly button: HTMLButtonElement;
  private readonly now: () => number;

  /** The line exactly as last sent. What the button does is decided by this and
   *  never by a flag of this panel's own -- a bite arrives unasked, and a line
   *  can expire the same way. */
  private drawn: Fishing | null = null;

  /** When the bite frame arrived, on this client's clock. The bar is drawn from
   *  it, and it is only ever used for drawing: the server measures the reaction
   *  time from its own tick, and this number never reaches the wire. */
  private bitAtMs = 0;

  constructor(
    container: HTMLElement,
    private readonly actions: FishingActions = {},
    now?: () => number,
  ) {
    this.now = now ?? (() => performance.now());
    const doc = container.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "div", "fishing");
    this.root.hidden = true;

    const title = elem(doc, "div", "fishing-title");
    const heading = doc.createElement("span");
    heading.textContent = "Fishing";
    this.state = elem(doc, "span", "fishing-state");
    title.append(heading, this.state);

    this.none = elem(doc, "div", "fishing-none", "No rod. The Fisherman sells one.");

    this.rod = elem(doc, "div", "fishing-rod");

    this.practiceLabel = elem(doc, "div", "fishing-meter-label");
    const practiceTrack = elem(doc, "div", "fishing-meter-track");
    this.practiceFill = elem(doc, "div", "fishing-meter-fill");
    practiceTrack.append(this.practiceFill);

    this.window_ = elem(doc, "div", "fishing-window");
    this.window_.hidden = true;
    this.windowFill = elem(doc, "div", "fishing-window-fill");
    this.window_.append(this.windowFill);

    this.last = elem(doc, "div", "fishing-last");

    this.button = doc.createElement("button");
    this.button.type = "button";
    this.button.className = "fishing-button";
    this.button.textContent = buttonLabel(FISHING_IDLE);
    this.button.addEventListener("click", () => this.press());

    this.root.append(
      title,
      this.none,
      this.rod,
      this.practiceLabel,
      practiceTrack,
      this.window_,
      this.last,
      this.button,
    );
    container.append(this.root);
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  /** The state this panel is currently drawing. For tests, and for a harness
   *  that has to wait for a bite it cannot predict. */
  get lineState(): number {
    return this.drawn === null ? FISHING_IDLE : this.drawn.state;
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
  }

  toggle(): void {
    this.root.hidden = !this.root.hidden;
  }

  /** Cast or pull, from anywhere -- the key handler uses this so that one
   *  gesture and one button cannot disagree about which they are. */
  press(): void {
    this.actions.onFish?.();
  }

  update(line: Fishing): void {
    const wasBiting = this.lineState === FISHING_BITING;
    this.drawn = line;

    if (line.state === FISHING_BITING && !wasBiting) this.bitAtMs = this.now();

    const has = line.rod_vnum !== 0;
    this.none.hidden = has;
    this.rod.hidden = !has;
    this.practiceLabel.hidden = !has;
    (this.practiceFill.parentElement as HTMLElement).hidden = !has;

    if (has) {
      this.rod.textContent = `Fishing level ${line.level}`;
      this.practiceLabel.replaceChildren();
      const left = this.root.ownerDocument.createElement("span");
      left.textContent = "Practice";
      const right = this.root.ownerDocument.createElement("span");
      right.textContent = `${line.practice} / ${line.practice_max}`;
      this.practiceLabel.append(left, right);
      this.practiceFill.style.width =
        line.practice_max > 0
          ? `${Math.round((line.practice / line.practice_max) * 100)}%`
          : "0%";
    }

    this.state.textContent =
      line.state === FISHING_BITING
        ? line.fish_name
          ? `a bite! ${line.fish_name}` // OnFishingNotify, sent only under PredictFish (fishing.cpp:486-493)
          : "a bite!"
        : line.state === FISHING_WAITING
          ? "waiting"
          : "";
    this.state.classList.toggle(
      "fishing-state-biting",
      line.state === FISHING_BITING,
    );

    this.window_.hidden = line.state !== FISHING_BITING;
    this.button.textContent = buttonLabel(line.state);
    this.button.classList.toggle(
      "fishing-button-biting",
      line.state === FISHING_BITING,
    );
    // Greyed only with no rod: casting is what a player does next in every
    // other state, and a button that vanished while waiting would make pulling
    // an empty line impossible -- which is a thing the original allows, and
    // losing the cast for it is the cost of guessing.
    this.button.disabled = !has;
  }

  /** Say what came of a pull. One sentence, replaced rather than accumulated:
   *  a feed of catches is what the chat log is for. */
  caught(result: FishCaught): void {
    this.last.textContent = describeCatch(result);
  }

  tick(): void {
    if (this.drawn === null || this.drawn.state !== FISHING_BITING) return;
    const elapsed = this.now() - this.bitAtMs;
    const fraction = Math.max(
      0,
      Math.min(1, 1 - elapsed / Math.max(1, this.drawn.window_ms)),
    );
    this.windowFill.style.width = `${Math.round(fraction * 100)}%`;
  }
}
